/**
 * Per-document overrides — the port of pipeline_common.py's overrides model.
 *
 * A reviewer's audit UI can attach, per document id ("<class>/<stem>"):
 *   - skip:   stage names to skip,
 *   - exclude: drop from TRAINING (the pipeline accepts/passes it through but
 *              does not act on it — exclusion only matters to the trainer),
 *   - rotate: a manual clockwise rotation (deg) applied on top of orientation.
 */

export type StageName = 'localize' | 'bgremove' | 'deskew' | 'orient';

export interface DocOverride {
  skip?: StageName[];
  exclude?: boolean;
  rotate?: number;
}

/** Map of document id → override. */
export type Overrides = Record<string, DocOverride>;

export interface OverrideContext {
  /** Document id used to look up overrides ("<class>/<stem>"), if any. */
  docId?: string;
  overrides?: Overrides;
}

function lookup(ctx: OverrideContext | undefined): DocOverride | undefined {
  if (!ctx?.docId || !ctx.overrides) return undefined;
  return ctx.overrides[ctx.docId];
}

/** Port of is_skipped(). */
export function isSkipped(ctx: OverrideContext | undefined, stage: StageName): boolean {
  return lookup(ctx)?.skip?.includes(stage) ?? false;
}

/** Port of rotate_override(): manual clockwise rotation, normalised to 0..359. */
export function rotateOverride(ctx: OverrideContext | undefined): number {
  const r = lookup(ctx)?.rotate ?? 0;
  return ((r % 360) + 360) % 360;
}

/** Port of exclude (training-only; pipeline passes it through). */
export function isExcluded(ctx: OverrideContext | undefined): boolean {
  return lookup(ctx)?.exclude ?? false;
}

/** Port of doc_id(): "<class>/<stem>" from a relative path. */
export function docId(rel: string): string {
  const noExt = rel.replace(/\.[^./\\]+$/, '');
  return noExt.replace(/\\/g, '/');
}
